import {blob} from './xhr.js';
import {log} from './environment.js';

const NATIVE_SUPPORT = typeof document !== 'undefined' && window.FontFace && document.fonts;

const FONT_CACHE = new Map();
const GROUP_CACHE = new WeakMap();

class Fonts {
	constructor(fonts) {
		this.fonts = fonts.map(mapFont);
		this.tryLoad();
	}

	tryLoad() {
		const notLoaded = this.fonts.filter(font => !font.loaded);
		this.loaded = !notLoaded.length;
		this.promise = Promise.all(notLoaded.map(font => tryLoad(font).promise))
			.then(() => {
				this.loaded = true;
				return this;
			});
		return this;
	}

	inject() {
		if (!IS_BROWSER || !NATIVE_SUPPORT) { return; }
		this.fonts.forEach(font => {
			font.references += 1;
			if (font.references === 1 && font.face) {
				document.fonts.add(font.face);
			}
		});
	}

	toCSS() {
		if (NATIVE_SUPPORT) { return null; }
		return this.fonts.map(style).join('\n');
	}

	release() {
		if (!IS_BROWSER || !NATIVE_SUPPORT) { return; }
		this.fonts.forEach(font => {
			font.references -= 1;
			if (font.references === 0 && font.face) {
				document.fonts.delete(font.face);
			}
		});
	}
}

function style({font, dataURL}) {
	return `@font-face {
	font-family: "${font.name}";
	src: url(${dataURL || `${font.url}?fofa`});
	font-weight: ${font.weight};
	font-style: ${font.style};
}`;
}

function fontFaceFromDefinition(
	font,
	forcedFormat,
	cacheBust
) {
	let url = font.url;

	if (forcedFormat) {
		const dIdx = font.url.lastIndexOf(".");
		if (dIdx >= 0) {
			url = `${url.substring(0, dIdx)}.${forcedFormat}`;
		}
	}

	if (cacheBust) {
		url = `${url}?${(Math.random() * 100000).toFixed(0)}`;
	}

	return new FontFace(font.name, `url(${url})`, {
		weight: font.weight,
		style: font.style,
		display: "swap",
	});
}

function mapFont(font) {
	if (!FONT_CACHE.has(font)) {
		const fontMeta = {
			font,
			face: null,
			references: 0,
			loaded: false,
			failed: false,
			dataURL: null,
			promise: null,
		};

		try {
			const face = NATIVE_SUPPORT && fontFaceFromDefinition(font);
			fontMeta.face = face;
		} catch (e) {
			console.error(e);
			fontMeta.failed = true;
		}
		FONT_CACHE.set(font, fontMeta);		
	}
	const meta = FONT_CACHE.get(font);
	return tryLoad(meta);
}

function tryLoad(meta) {
	if (!IS_BROWSER || meta.loaded || (meta.promise && !meta.failed)) {
	  return meta;
	}
	const {face, font} = meta;
	const load = () => {
	  const p = new Promise(resolve => {
		let retries = 3;

		const retryOrFail = (fontFace, err) => {
			let targetFace =
				retries === 3 ? fontFace : fontFaceFromDefinition(font);

			switch (retries) {
			case 0:
				console.log(`failed to load ${font.name} (${font.url}): ${err}`);
				resolve(null);
				break;
			case 1:
				targetFace = fontFaceFromDefinition(
					font,
					"woff",
					// safari, for example, caches fonts for incredibly long times,
					// so we try a cache buster on the final attempt
					true
				);
			default:
				retries -= 1;
				setTimeout(() => {
					loadFn(targetFace);
				}, (3 - retries) * 150);
				break;
			}
		};

		const loadFn = fontFace => {
		  try {
			fontFace
			  .load()
			  .then(() => {
				meta.face = fontFace;
				resolve(null);
			  })
			  .catch(err => {
				retryOrFail(fontFace, err);
			  });
		  } catch (err) {
			retryOrFail(fontFace, err);
		  }
		};

		setTimeout(() => loadFn(face), 0);
	  });

	  return p;
	};

	const promise = face
	  ? (load())
	  : (blob(font.url)
		  .then(toDataURL)
		  .then(dataURL => (meta.dataURL = dataURL)));

	meta.promise = promise
		.then(() => (meta.loaded = true))
		.catch(() => {
			console.error(`Kent: loading font "${font.url}" failed, but ignoring`);
			log("loading font failed", {
				level: "warning",
				extra: {url: font.url},
				fingerprint: ["loading", "font"],
			});
			meta.failed = true;
		});

	return meta;
}

function toDataURL(blob) {
	return new Promise(resolve => {
		const reader = new FileReader();
		reader.onloadend = () => {
			resolve(reader.result);
		};
		reader.readAsDataURL(blob);
	});
}

const NOOP = [];

function getCachedGroup(fonts = NOOP) {
	if (!GROUP_CACHE.has(fonts)) {
		GROUP_CACHE.set(fonts, new Fonts(fonts));
	}
	const group = GROUP_CACHE.get(fonts);
	return group.tryLoad();
}

export default getCachedGroup;



// WEBPACK FOOTER //
// ./lib/fonts.js